import styles from "./BillingCardsCreate.module.scss";
import React, { useEffect, useState } from "react";
import { Form } from "antd";
import FormItemInput from "../../../../components/ui/FormItemInput/FormItemInput";
import { useTranslation } from "react-i18next";
import CustomButton from "../../../../components/ui/CustomButton/CustomButton";
import useAppSelector from "../../../../hooks/useAppSelector";
import { IUser } from "../../../../models/User";
import { EncryptionService } from "../../../../services/encryption.service";
import { handleError } from "../../../../helper/errorHandler";
import useAppDispatch from "../../../../hooks/useAppDispatch";
import { useNavigate } from "react-router-dom";
import { usePaymentMethodsMutations } from "../../../../api/mutations/usePaymentMethodsMutations";

const BillingCardsCreate = () => {
  const user = useAppSelector((state) => state.userProfileSlice.user) as IUser;

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { saveCard } = usePaymentMethodsMutations();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    form.setFieldsValue({
      nameOnCard: user.firstname + " " + user.lastname,
    });
  }, []);

  const handleSubmit = async (values: {
    cardNumber: string;
    expiry: string;
    cvc: string;
    nameOnCard: string;
  }) => {
    try {
      setIsSubmitting(true);
      const encryptedData = await formatAndEncryptCard(values);
      const redirectUrl = "https://clubpass.gr";
      saveCard(
        { userId: user.id, cardDetails: encryptedData, redirectUrl },
        {
          onSuccess: () => navigate("/settings/billing/cards?message=CardAddedSuccessfully"),
          onError: (e) => handleError(e, dispatch, t),
          onSettled: () => setIsSubmitting(false),
        }
      );
    } catch (e) {
      handleError(e, dispatch, t);
      setIsSubmitting(false);
    }
  };

  const formatAndEncryptCard = async (values: {
    cardNumber: string;
    expiry: string;
    cvc: string;
    nameOnCard: string;
  }) => {
    const [month, year] = values.expiry.split("/");
    const card = {
      nameOnCard: values.nameOnCard,
      cardNumber: values.cardNumber,
      expiryDate: `${month}/20${year}`,
      cvv: values.cvc,
    };
    return await EncryptionService.encrypt(card);
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>{t("BillingInformation")}</h2>
      <Form onFinish={handleSubmit} form={form} layout="vertical" autoComplete="off">
        <FormItemInput name={"nameOnCard"} label={t("NameOnCard")} rules={{ required: true }} />
        <FormItemInput
          maxLength={16}
          placeholder={"1234 5678 9012 3456"}
          name={"cardNumber"}
          label={t("CardNumber")}
          rules={[
            { required: true, message: "Card Number is required" },
            {
              pattern: /^\d{16}$/,
              message: "Invalid card number",
            },
          ]}
        />
        <div className={styles.row}>
          <FormItemInput
            maxLength={5}
            placeholder="MM/YY"
            name={"expiry"}
            label={t("ExpirationDate")}
            rules={[
              { required: true, message: "Expiry date is required" },
              { pattern: /^(0[1-9]|1[0-2])\/\d{2}$/, message: "Invalid expiry format" },
            ]}
          />
          <FormItemInput
            maxLength={3}
            placeholder="123"
            name={"cvc"}
            label={"CVC"}
            rules={[
              { required: true, message: "CVC is required" },
              { pattern: /^\d{3}$/, message: "Invalid CVC" },
            ]}
          />
        </div>
        <CustomButton
          text={t("SaveCard")}
          type={"submit"}
          typeStyle={"fill"}
          style={{ maxWidth: "243px" }}
          showLoading={isSubmitting}
        />
      </Form>
    </div>
  );
};

export default BillingCardsCreate;
